import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheckCircle } from "react-icons/fa";
import api from "../services/api";
import { getShippingConfig, calcShipping } from "../services/shipping";
import { trackPurchase } from "../services/analytics";

const OrderSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    orderId,
    orderNumber = null,
    total,
    paymentMethod = "cash",
    paymentSuccess = false,
    transactionId = null,
    authCode = null,
    message = null
  } = location.state || {};

  // Human-friendly reference shown to the customer (matches the confirmation email).
  const displayOrderId = orderNumber || orderId;

  const [shipCfg, setShipCfg] = useState({ fee: 5000, threshold: 50000 });
  useEffect(() => { getShippingConfig().then(setShipCfg); }, []);
  const ship = calcShipping(total, shipCfg);

  // trackPurchase de-dupes on orderId itself, so this is safe even though the
  // effect can re-run (e.g. React StrictMode's double-invoke in dev).
  useEffect(() => {
    if (orderId && total) trackPurchase({ orderId, total });
  }, [orderId, total]);

  const [screenshot, setScreenshot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");

  const handleFileChange = (e) => {
    setScreenshot(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!screenshot) {
      toast.warning(t("success.selectImage"));
      return;
    }

    const formData = new FormData();
    formData.append("screenshot", screenshot);

    try {
      setUploading(true);
      const res = await api.post(`/orders/${orderId}/upload-proof`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setUploadedUrl(res.data.url);
      toast.success(t("success.uploadOk"));
    } catch (err) {
      toast.error(t("success.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const renderBasicSuccess = () => (
    <Card border="warning" className="text-center shadow-sm p-4">
      <FaCheckCircle size={80} style={{ display: "block", margin: "0 auto" }} className="text-warning mb-3" />
      <h3 className="text-success fw-bold">{t("success.title")}</h3>
      <p className="text-muted fs-5 mt-3">
        {t("success.emailNote")}
      </p>
      <p className="mt-3">
        {t("success.checkStatus")}
        <br />
        {t("success.guestNote")}
      </p>
      <Button
        variant="warning"
        size="lg"
        className="mt-4"
        onClick={() => navigate("/")}
      >
        {t("cart.continueShopping")}
      </Button>
    </Card>
  );

  return (
    <Container className="my-5">
      <ToastContainer position="bottom-right" />
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8}>
          {!orderId || !total ? (
            renderBasicSuccess()
          ) : (
            <Card border="warning" className="shadow-sm">
              <Card.Body>
                <div className="text-center mb-4">
                  <FaCheckCircle size={80} className="text-warning" />
                  <h3 className="text-success fw-bold mt-3">🎉 {t("success.title")}</h3>
                  <p className="text-muted">
                    {t("success.orderIdIs")} <strong>{displayOrderId}</strong>
                  </p>
                  {paymentSuccess && transactionId && (
                    <Alert variant="success" className="mt-3">
                      <strong>{t("success.paymentConfirmed")}</strong>
                      <br />
                      {t("success.transactionId")}: {transactionId}
                      {authCode && <><br />{t("success.authCode")}: {authCode}</>}
                      {message && <><br />{message}</>}
                    </Alert>
                  )}
                </div>

                <hr />

                <h5 className="text-warning">{t("cart.orderSummary")}</h5>
                <Row className="mb-3">
                  <Col xs={6}>{t("cart.subtotal")}:</Col>
                  <Col xs={6} className="text-end">￦{(total - ship).toLocaleString()}</Col>
                </Row>
                <Row className="mb-3">
                  <Col xs={6}>{t("cart.shipping")}:</Col>
                  <Col xs={6} className="text-end">
                    {ship === 0 ? (
                      <span className="text-success">{t("cart.free")}</span>
                    ) : (
                      `￦${ship.toLocaleString()}`
                    )}
                  </Col>
                </Row>
                <Row className="mb-3 border-top pt-2">
                  <Col xs={6}><strong>{t("success.totalPaid")}:</strong></Col>
                  <Col xs={6} className="text-end"><strong>￦{total.toLocaleString()}</strong></Col>
                </Row>
                <Row className="mb-3">
                  <Col xs={6}>{t("success.paymentMethod")}:</Col>
                  <Col xs={6} className="text-end text-capitalize">
                    {paymentMethod === "account_transfer" && t("success.accountTransfer")}
                    {paymentMethod === "cash" && t("success.cashOnDelivery")}
                    {paymentMethod === "inicis_card" && t("success.cardPayment")}
                  </Col>
                </Row>

                {paymentMethod === "account_transfer" && !paymentSuccess && (
                  <div className="mt-4 p-3 border bg-light rounded">
                    <h6 className="text-warning">{t("success.uploadProof")}</h6>
                    <p className="mb-2">{t("success.uploadProofText")}</p>

                    <Form.Group controlId="screenshotUpload" className="mb-2">
                      <Form.Control type="file" accept="image/*" onChange={handleFileChange} />
                    </Form.Group>
                    <Button
                      variant="warning"
                      disabled={uploading}
                      onClick={handleUpload}
                    >
                      {uploading ? t("success.uploading") : t("success.uploadBtn")}
                    </Button>

                    {uploadedUrl && (
                      <Alert variant="success" className="mt-3">
                        {t("success.uploaded")} ✅<br />
                        <a href={uploadedUrl} target="_blank" rel="noreferrer">{t("success.viewUploaded")}</a>
                      </Alert>
                    )}
                  </div>
                )}

                {paymentMethod === "inicis_card" && paymentSuccess && (
                  <Alert variant="info" className="mt-4">
                    <strong>{t("success.paymentComplete")}</strong>
                    <p className="mb-0">
                      {t("success.cardProcessed")}
                    </p>
                  </Alert>
                )}

                <div className="mt-5 text-center">
                  <p className="text-muted fs-6">
                    {t("success.emailNote")}
                    <br />
                    {t("success.trackNote")}
                  </p>
                  <Button variant="outline-dark" onClick={() => navigate("/")}>
                    {t("cart.continueShopping")}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default OrderSuccess;